import jsPDF from "jspdf";

const LOGO_URLS = ["/logo.png", "/contovia-logo.png", "/nexaflow-logo.png", "/crismar-logo.png"];
const NAVY = [15, 41, 66]; // #0F2942
const SLATE = [71, 85, 105];

// Preload logo as base64 (or null on failure)
async function loadLogo() {
  for (const url of LOGO_URLS) {
    try {
      const res = await fetch(url);
      if (!res.ok) continue;
      const blob = await res.blob();
      return await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result);
        r.onerror = reject;
        r.readAsDataURL(blob);
      });
    } catch {
      // try next
    }
  }
  return null;
}

const formatDate = (iso) => {
  if (!iso) return "—";
  if (iso.length === 10) {
    const [y, m, d] = iso.split("-");
    return `${d}/${m}/${y}`;
  }
  try {
    return new Date(iso).toLocaleString("pt-BR");
  } catch {
    return iso;
  }
};

const s = (v) => (v == null || v === "" ? "—" : String(v));

export async function exportClientPDF(client, report) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const marginX = 40;
  const marginBottom = 60;
  let y = 40;

  const logo = await loadLogo();
  if (logo) {
    try {
      doc.addImage(logo, "PNG", marginX, y, 44, 44);
    } catch {
      // ignore image errors
    }
  }

  // Title & date (top-right)
  doc.setTextColor(...NAVY);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text("Ficha de Cliente", marginX + 56, y + 20);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(...SLATE);
  doc.text("Contovia", marginX + 56, y + 36);

  const exportedAt = new Date().toLocaleString("pt-BR");
  doc.text(
    `Exportado em: ${exportedAt}`,
    pageW - marginX,
    y + 20,
    { align: "right" }
  );

  y += 60;
  doc.setDrawColor(...NAVY);
  doc.setLineWidth(1.5);
  doc.line(marginX, y, pageW - marginX, y);
  y += 20;

  const ensureSpace = (needed) => {
    if (y + needed > pageH - marginBottom) {
      doc.addPage();
      y = 40;
    }
  };

  const section = (title) => {
    ensureSpace(30);
    doc.setFillColor(15, 41, 66);
    doc.rect(marginX, y - 12, pageW - marginX * 2, 20, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text(title.toUpperCase(), marginX + 8, y + 2);
    y += 20;
    doc.setTextColor(...SLATE);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
  };

  const kv = (rows) => {
    // rows: [[label, value], ...] rendered in 2 columns
    const colW = (pageW - marginX * 2) / 2;
    for (let i = 0; i < rows.length; i += 2) {
      ensureSpace(30);
      const [l1, v1] = rows[i];
      const [l2, v2] = rows[i + 1] || ["", ""];
      doc.setFont("helvetica", "bold");
      doc.setTextColor(...NAVY);
      doc.setFontSize(8);
      doc.text(l1.toUpperCase(), marginX, y);
      if (l2) doc.text(l2.toUpperCase(), marginX + colW, y);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(30, 30, 30);
      doc.setFontSize(10);
      const v1Lines = doc.splitTextToSize(s(v1), colW - 10);
      const v2Lines = l2 ? doc.splitTextToSize(s(v2), colW - 10) : [];
      doc.text(v1Lines, marginX, y + 12);
      if (l2) doc.text(v2Lines, marginX + colW, y + 12);
      const maxLines = Math.max(v1Lines.length, v2Lines.length, 1);
      y += 12 + maxLines * 12 + 6;
    }
    y += 4;
  };

  const paragraph = (label, value) => {
    ensureSpace(24);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...NAVY);
    doc.setFontSize(8);
    doc.text(label.toUpperCase(), marginX, y);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(30, 30, 30);
    doc.setFontSize(10);
    const lines = doc.splitTextToSize(s(value), pageW - marginX * 2);
    doc.text(lines, marginX, y + 12);
    y += 12 + lines.length * 12 + 6;
  };

  // ----- Identificação -----
  section("Identificação");
  kv([
    ["Razão Social", client.name],
    ["Nome Fantasia", client.nome_fantasia],
    ["CNPJ", client.document],
    ["CNAE", client.cnae],
    ["Data de Constituição", formatDate(client.data_constituicao)],
    ["Inscrição Estadual", client.inscricao_estadual],
    ["Inscrição Municipal", client.inscricao_municipal],
    ["Regime Tributário", client.regime_tributario],
  ]);

  // ----- Endereço -----
  section("Endereço");
  kv([
    ["CEP", client.endereco_cep],
    ["Rua", client.endereco_rua],
    ["Número", client.endereco_numero],
    ["Complemento", client.endereco_complemento],
    ["Bairro", client.endereco_bairro],
    ["Cidade", client.endereco_cidade],
    ["UF", client.endereco_uf],
    ["", ""],
  ]);

  // ----- Contatos -----
  section("Contatos");
  kv([
    ["Sócio (Nome)", client.contato_socio_nome],
    ["Sócio (E-mail)", client.contato_socio_email],
    ["Sócio (WhatsApp)", client.contato_socio_whatsapp],
    ["Financeiro (Nome)", client.contato_financeiro_nome],
    ["Financeiro (E-mail)", client.contato_financeiro_email],
    ["Financeiro (WhatsApp)", client.contato_financeiro_whatsapp],
  ]);

  // ----- Certificado Digital -----
  section("Certificado Digital");
  kv([
    ["Tipo de Certificado", client.tipo_certificado],
    ["Data de Validade", formatDate(client.validade_certificado)],
  ]);

  // ----- Dados Bancários -----
  section("Dados Bancários");
  kv([
    ["Banco", client.banco],
    ["Agência", client.agencia],
    ["Conta", client.conta],
    ["Tipo de Conta", client.tipo_conta],
  ]);

  // ----- Observações -----
  if (client.observacoes) {
    section("Observações (Perfil do Cliente)");
    paragraph("Observações", client.observacoes);
  }

  // ----- Ficha de Processo (opcional) -----
  if (report && (report.company_data || report.partners?.length || report.process_progress || report.licenses)) {
    section("Ficha de Processo — Dados da Empresa");
    const cd = report.company_data || {};
    kv([
      ["Data de Início", formatDate(cd.data_inicio)],
      ["Razão Social", cd.razao_social],
      ["CNAE", cd.cnae],
      ["N° IPTU", cd.iptu],
      ["Endereço", cd.endereco],
      ["Capital Social", cd.capital_social],
      ["Ponto de Referência", cd.ponto_referencia],
      ["", ""],
    ]);

    if (report.partners?.length) {
      section("Composição Societária");
      const partnerRows = [];
      report.partners.forEach((p, i) => {
        partnerRows.push([`Sócio ${i + 1}`, p.nome], ["Participação", p.participacao]);
      });
      kv(partnerRows);
    }

    const pp = report.process_progress || {};
    section("Andamento do Processo");
    kv([
      ["N° REGIN", pp.regin_numero],
      ["Data REGIN", formatDate(pp.regin_data)],
      ["Data REDESIM", formatDate(pp.redesim_data)],
      ["N° Protocolo Web", pp.protocolo_web_numero],
      ["Data Protocolo Web", formatDate(pp.protocolo_web_data)],
      ["", ""],
    ]);
    if (pp.observacoes) paragraph("Observações", pp.observacoes);

    const lic = report.licenses || {};
    section("Situação das Licenças");
    kv([
      ["Situação CBMERJ", lic.cbmerj],
      ["Situação VISA", lic.visa],
      ["Situação SMMA", lic.smma],
      ["ALVARÁ", lic.alvara],
    ]);
  }

  // ----- Auditoria -----
  ensureSpace(40);
  y += 6;
  doc.setDrawColor(200, 200, 200);
  doc.setLineWidth(0.5);
  doc.line(marginX, y, pageW - marginX, y);
  y += 14;
  doc.setFont("helvetica", "italic");
  doc.setFontSize(9);
  doc.setTextColor(...SLATE);
  const auditBy = client.last_updated_by || report?.last_updated_by;
  const auditAt = client.last_updated_at || report?.last_updated_at;
  if (auditBy && auditAt) {
    doc.text(
      `Última atualização feita por: ${auditBy} em ${formatDate(auditAt)}`,
      marginX,
      y
    );
    y += 12;
  }
  doc.text(`Documento exportado em ${exportedAt}`, marginX, y);

  const safeName = (client.name || "cliente").replace(/[^a-z0-9]+/gi, "_").toLowerCase();
  doc.save(`ficha_${safeName}.pdf`);
}
